import type { SheetPolylineGeometry } from "building-types/views/sheetGeometryTypes";
import { sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import { ZERO_INCHES } from "common/constants/scaleFactors";
import type { SheetInches, SheetPoints } from "common/types/nominalNumbersTypes";
import { polyLine2Copy, polyLine2Move } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";

interface GradeLineProps {
  x: SheetInches;
  y: SheetInches;
  polyLine: PolyLine2;
  /**
   * Only set this for outputs that never run the geometry through a category stylesheet (the
   * brochure overlay). Sheet geometry wins over its stylesheet in `applyStylesToGeometry`, so
   * setting it anywhere else would make the View Template's Grade Line width unusable.
   */
  lineWidth?: SheetPoints;
}

const gradeLineDefault: Omit<GradeLineProps, "lineWidth"> = {
  x: ZERO_INCHES,
  y: ZERO_INCHES,
  polyLine: polyLine2New([0, 0, 50 / 72, 10 / 72, 75 / 72, -2 / 72, 100 / 72, 0]),
};

/**
 * Builds the grade line as a stroked open polyline. Stroke width, color and dash come from the
 * GRADE_LINE category stylesheet -- overridable per View Template -- rather than from this
 * geometry, so style edits actually take effect.
 */
export function generateGradeLineSheetGeometry(inProps: Partial<GradeLineProps>): SheetPolylineGeometry {
  const props = { ...gradeLineDefault, ...inProps };

  const polyLine = polyLine2Copy(props.polyLine);
  polyLine2Move(polyLine, props.x, props.y);

  return sheetPolylineGeometryNew({
    paths: [polyLine],
    linewidth: props.lineWidth,
    // Pinned rather than left to the stylesheet. GRADE_LINE was a FILL category before this became
    // a stroked line, so a View Template may still carry a `fill: true` override, which cascades
    // above the category default and can no longer be cleared now that the fill controls are hidden
    // for LINE categories. Both renderers close an open path before filling it
    // (`polyLine2Close` in styledPolyLineToTHREE, the implicit close in PDF `fillAndStroke`), so the
    // result would be a filled wedge back to the first point -- and renderPDFPolylines already
    // documents printer artifacts from non-closed fill+stroke paths. Geometry outranks every
    // stylesheet, so setting it here makes that unreachable. Remove only if GRADE_LINE becomes a
    // FILL category again.
    fill: false,
  });
}
